import React, { useEffect, useMemo, useState } from "react";
import api from "@api";
import { NavLink, useParams } from "react-router-dom";
import "./DirectoryLeaguePlayer.css";

function DirectoryLeaguePlayer() {
  const { leagueNameAndId, rosterId } = useParams();
  const directoryLeagueId = leagueNameAndId?.split("-").pop();

  const [league, setLeague] = useState(null);
  const [playerPage, setPlayerPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlayerPage = async () => {
      try {
        const [leagueRes, playerRes] = await Promise.all([
          api.get(`/api/directory/leagues/${directoryLeagueId}`),
          api.get(`/api/directory/leagues/${directoryLeagueId}/players/${rosterId}`),
        ]);

        setLeague(leagueRes.data);
        setPlayerPage(playerRes.data);
      } catch (err) {
        console.error("Failed to fetch directory player page", err);
      } finally {
        setLoading(false);
      }
    };

    if (directoryLeagueId && rosterId) {
      fetchPlayerPage();
    }
  }, [directoryLeagueId, rosterId]);

  const formatDate = (date) => {
    if (!date) return "TBD";

    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const player = playerPage?.player;
  const team = playerPage?.team;
  const season = playerPage?.season;

  const rawStats = playerPage?.stats || [];
  const gameLog = playerPage?.gameLog || [];
  const statsPublic = playerPage?.statsVisibility?.public;

  const playerName =
    `${player?.firstName || ""} ${player?.lastName || ""}`.trim() || "Player";

  const statTotals = useMemo(() => {
    const totals = {};

    rawStats.forEach((stat) => {
      const label = stat.abbreviation || stat.name || "Stat";

      if (!totals[label]) {
        totals[label] = {
          label,
          name: stat.name,
          value: 0,
        };
      }

      totals[label].value += Number(stat.value || 0);
    });

    return Object.values(totals);
  }, [rawStats]);

  const gameStatsByGameId = useMemo(() => {
    const grouped = {};

    rawStats.forEach((stat) => {
      const gameId = stat.gameId;
      const label = stat.abbreviation || stat.name || "Stat";

      if (!grouped[gameId]) {
        grouped[gameId] = {};
      }

      grouped[gameId][label] = (grouped[gameId][label] || 0) + Number(stat.value || 0);
    });

    return grouped;
  }, [rawStats]);

  const statColumns = statTotals.map((stat) => stat.label);


  if (loading) {
    return <div className="directory-player-page">Loading player...</div>;
  }

  if (!playerPage) {
    return <div className="directory-player-page">Player not found.</div>;
  }



  return (
    <div className="league-profile-page">
      <div className="league-profile-container">
        <div className="league-profile-public-tabs">
          <NavLink end to={`/leagues/${leagueNameAndId}`}>
            Overview
          </NavLink>

          <NavLink to={`/leagues/${leagueNameAndId}/schedule`}>
            Schedule
          </NavLink>

          <NavLink to={`/leagues/${leagueNameAndId}/standings`}>
            Standings
          </NavLink>

          <NavLink to={`/leagues/${leagueNameAndId}/players`}>
            Players
          </NavLink>
        </div>

        <div className="league-profile-card directory-player-card">
          {playerPage?.locked ? (
            <div className="directory-player-empty">
              Player pages are available with League Tools.
            </div>
          ) : (
            <>
              <div className="directory-player-header">
                <div>
                  <h1 className="directory-player-title">{playerName}</h1>
                </div>
              </div>

              <div className="directory-player-summary">
                {team?.id ? (
                  <NavLink
                    to={`/leagues/${leagueNameAndId}/teams/${team.id}`}
                    className="directory-player-summary-box directory-player-summary-link"
                  >
                    <span>Team</span>
                    <strong>{team?.team?.name || team?.name || "Team TBD"}</strong>
                  </NavLink>
                ) : (
                  <div className="directory-player-summary-box">
                    <span>Team</span>
                    <strong>Team TBD</strong>
                  </div>
                )}

                <div className="directory-player-summary-box">
                  <span>Games Played</span>
                  <strong>{playerPage?.gamesPlayed || 0}</strong>
                </div>

                <div className="directory-player-summary-box">
                  <span>Season</span>
                  <strong>{season?.name || "Current Season"}</strong>
                </div>
              </div>

              {!statsPublic ? (
                <div className="directory-player-empty">
                  Player stats are not publicly displayed for this season.
                </div>
              ) : rawStats.length === 0 ? (
                <div className="directory-player-empty">
                  Player stats will appear once game results are updated.
                </div>
              ) : (
                <>
                

                  <div className="directory-player-section">
                    <h2>Totals</h2>

                    <div className="directory-player-totals-row">
                      {statTotals.map((stat) => (
                        <div key={stat.label} className="directory-player-total-box">
                          <span>{stat.label}</span>
                          <strong>{stat.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="directory-player-section">
                    <h2>Game Log</h2>

                    {gameLog.length > 0 ? (
                      <div className="directory-player-game-log-table">
                        <div className="directory-player-game-log-header">
                          <div>Date</div>
                          <div>Opponent</div>
                          <div>Result</div>

                          {statColumns.map((column) => (
                            <div key={column}>{column}</div>
                          ))}
                        </div>

                        {gameLog.map((game) => {
                          const gameStats = gameStatsByGameId[game.gameId] || {};

                          return (
                            <div key={game.gameId} className="directory-player-game-log-row">
                              <div>{formatDate(game.date)}</div>
                              <div>{game.opponent || "Opponent TBD"}</div>
                              <div>{game.result || "Result pending"}</div>

                              {statColumns.map((column) => (
                                <div key={column}>{gameStats[column] || 0}</div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="directory-player-empty">
                        Game log will appear once game stats are added.
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DirectoryLeaguePlayer;